// Miky Motorsport - denni vyzva (J): deterministicky z data vybrana trat + rival + cas.
// Cista logika (bez DOM/THREE) - testovatelne v Node. Seed = FNV hash retezce data,
// takze stejny den = stejna vyzva pro vsechny, ruzne dny = ruzne vyzvy.
import { hashStr } from './ghost.js';

// Pojmenovani rivalove (shoduji se s AI_DEFS v race.js) - koho dnes honis.
export const DAILY_RIVALS = ['Falko', 'Brutus', 'Vento', 'Apex', 'Dust'];

// B4: kdyz ma hrac na trati nejlepsi kolo, rival jede jeho tempem o tolik rychleji
// ("poraz sve minule ja o kousek"). 0.04 = 4 % rychlejsi nez PB tempo.
export const DAILY_PB_MARGIN = 0.04;

// Nejlepsi cas kola (ms) hrace na dane trati napric vsemi TT delkami; null kdyz zadny.
export function bestLapMsForTrack(timeTrials, trackId) {
  if (!timeTrials) return null;
  let best = null;
  for (const key in timeTrials) {
    if (key.indexOf(`${trackId}_`) !== 0) continue;
    const b = timeTrials[key] && timeTrials[key].bestLapMs;
    if (b > 0 && (best == null || b < best)) best = b;
  }
  return best;
}

// "YYYY-MM-DD" z Date (lokalni cas). new Date() v runtime hry je OK (jen ne ve workflow skriptu).
export function dateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Den predtim (pro streak) - "YYYY-MM-DD" -> "YYYY-MM-DD" o den drive.
export function prevDateKey(dayStr) {
  const [y, m, d] = dayStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - 1);
  return dateKey(dt);
}

// Vyzva pro dany den. trackOrder = pole id trati; refLapOf(id) -> sekundy.
// Vraci { dayStr, trackId, laps, rival, targetMs, rivalMul, personal }.
// pbLapMsOf(trackId) -> nejlepsi cas kola hrace (ms) nebo null/0. Kdyz existuje, cil =
// hracovo tempo o DAILY_PB_MARGIN rychleji (PB +X% -> "poraz sve minule ja"); jinak sdileny
// referencni cil. Trat/rival/kola jsou vzdy stejne ze seedu (denni "event" sdileny pro vsechny).
export function dailyChallenge(dayStr, trackOrder, refLapOf, pbLapMsOf = null) {
  if (!trackOrder || trackOrder.length === 0) return null;
  const h = hashStr(dayStr);
  const trackId = trackOrder[h % trackOrder.length];
  // 2..4 kol. Delitel 7 davala vyrazne mene 3kolovych dni (bias z interakce hashe a /7);
  // /13 je napric lety rovnomernejsi rozlozeni pres {0,1,2}.
  const laps = 2 + (Math.floor(h / 13) % 3);
  const rival = DAILY_RIVALS[Math.floor(h / 31) % DAILY_RIVALS.length];
  const refLap = refLapOf(trackId) || 30;
  // referencni rivaluv cas = mezi gold (0.93) a silver (1.02) - solidni, ale zvladnutelny.
  // rivalMul zaokrouhlime DRIV a z TEHOZ cisla pocitame targetMs, aby byly konzistentni.
  const rivalMul = +(0.95 + (Math.floor(h / 101) % 7) * 0.01).toFixed(2); // 0.95..1.01
  let targetMs = Math.round(refLap * laps * rivalMul * 1000);
  let personal = false;
  const pbLap = pbLapMsOf ? pbLapMsOf(trackId) : null;
  if (pbLap > 0) {
    const stretch = Math.round(pbLap * laps * (1 - DAILY_PB_MARGIN));
    const floor = Math.round(refLap * laps * 0.85 * 1000); // anti-impossible: ne rychleji nez 85 % reference
    targetMs = Math.max(stretch, floor);
    personal = true;
  }
  return { dayStr, trackId, laps, rival, targetMs, rivalMul, personal };
}

// Aktualizace stavu denni vyzvy v profilu po dokonceni jizdy.
//   prev = profile.daily nebo undefined; dayStr = dnesni den; finishMs = cas hrace;
//   targetMs = cilovy cas rivala. Vraci NOVY stav (nemutuje prev).
// Streak = pocet po sobe jdoucich dni s porazenym rivalem (vyhra navazujici na vcerejsek).
export function updateDailyState(prev, dayStr, finishMs, targetMs) {
  const p = prev && typeof prev === 'object' ? prev : {};
  // novy den -> resetuj denni PB (ale streak/historie zustava)
  const sameDay = p.date === dayStr;
  const prevBest = sameDay ? (p.bestMs ?? null) : null;
  const bestMs = (finishMs > 0) ? (prevBest == null ? finishMs : Math.min(prevBest, finishMs)) : prevBest;
  const beatenToday = bestMs != null && targetMs != null && bestMs <= targetMs;
  const wasWonToday = sameDay && !!p.wonToday;
  let streak = p.streak || 0;
  let lastWonDate = p.lastWonDate || null;
  // streak aktualizuj jen pri PRVNI dnesni vyhre (ne opakovane)
  if (beatenToday && !wasWonToday) {
    streak = (lastWonDate === prevDateKey(dayStr)) ? streak + 1 : 1;
    lastWonDate = dayStr;
  }
  return {
    date: dayStr,
    bestMs,
    wonToday: beatenToday || wasWonToday,
    streak,
    lastWonDate,
  };
}

// Skutecne AKTIVNI streak pro zobrazeni: serie zustava ziva, jen kdyz posledni vyherni
// den byl dnes nebo vcera; jinak je preruseny -> 0. (updateDailyState resetuje na 1 az
// pri PRISTI navazujici vyhre, takze ulozena hodnota muze byt po prohre/novem dni stara -
// zobrazeni musi pouzit tuto efektivni hodnotu, ne syrove daily.streak.)
export function effectiveStreak(daily, todayStr) {
  if (!daily || !daily.lastWonDate) return 0;
  const lw = daily.lastWonDate;
  if (lw === todayStr || lw === prevDateKey(todayStr)) return daily.streak || 0;
  return 0;
}
